import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ContentType } from "../shared/contentTypes";
import { LikeDataModel, LikeSyncDataModel, SetLikeModel } from "../shared/models/like.model";
import Utils from "../shared/utils";
import { CoreLogService } from "./log.service";
import { CoreCOMService } from "./com.service";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { IObjectObserver } from "./IObjectObserver";
import { ObjectObserverFactory } from "./objectObserverFactory";

@Injectable({
  providedIn: "root"
})
export class LikeService {
  private _likeObsServ: IObjectObserver<LikeDataModel>;

  constructor(private _coreLogService: CoreLogService,
    private _coreCOMService: CoreCOMService,
    private _objObsInjectHelper: CircularInjectionHelper) {
    const me = this;
    me._likeObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<LikeDataModel>(ContentType.Like, me._objObsInjectHelper);
  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._likeObsServ.ObserveChanges;
  }

  public SetLike(like: boolean, contentType: ContentType, contentID: number): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      try {
        if (me._coreCOMService.environment.networkInformation.isOnline) {
          me.SendLikeToServer(like, contentType, contentID)
            .then((SyncDataModel) => {
              // vorab speichern, so wird etwas zeit eingesparrt
              me._likeObsServ.SetObjects(SyncDataModel.likes)
                .then(resolve)
                .catch((error) => {
                  me._coreLogService.error("LikeService.SetLike", "SetObjects", error);
                  // try fallback and save it local
                  me.FallbackSetLikeLocal(like, contentType, contentID)
                    .then(resolve)
                    .catch(reject);
                });
            })
            .catch((error) => { me._coreLogService.error("LikeService.SetLike", "RequestService", error); reject(); });
        } else {
          me.FallbackSetLikeLocal(like, contentType, contentID)
            .then(resolve)
            .catch(reject);
        }
      } catch (error) {
        me._coreLogService.error("LikeService.SetLike", "try catch", error);
        reject();
      }
    });
  }

  private FallbackSetLikeLocal(like: boolean, contentType: ContentType, contentID: number): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      try {
        if (me._coreCOMService.environment.client.isMobile) {
          // setze es vorerst im storage mit Flag zum synchronesieren
          me._likeObsServ.GetObject(contentType + "|" + contentID)
            .then((model) => {
              if (Utils.IsNull(model)) {
                model = new LikeDataModel();
                model.content_type = contentType;
                model.content_id = contentID;
              }
              model.deleted = !like;
              model.DM_iPE = 1; // markiere zur synchronisation
              me._likeObsServ.SetObject(model)
                .then(resolve)
                .catch((error) => { me._coreLogService.error("LikeService.FallbackSetLikeLocal", "SetObjects", error); reject(); });
            }).catch((error) => { me._coreLogService.error("LikeService.FallbackSetLikeLocal", "GetObject", error); reject(); });
        } else {
          // browser can only work online
          reject("browser need internet");
        }
      } catch (error) {
        me._coreLogService.error("LikeService.FallbackSetLikeLocal", "try catch", error);
        reject();
      }
    });
  }

  private SendLikeToServer(like: boolean, contentType: ContentType, contentID: number): Promise<LikeSyncDataModel> {
    const me = this;
    return new Promise<LikeSyncDataModel>((resolve, reject) => {
      try {
        const RequestData = new SetLikeModel();
        RequestData.content_type = contentType;
        RequestData.content_id = contentID;
        RequestData.like = like;
        const FunctionName = "like/SetLike";
        const SyncDataModelConstructor = LikeSyncDataModel;
        me._objObsInjectHelper.yourAppServer.Api.RequestService(SyncDataModelConstructor, FunctionName, RequestData)
          .then((response) => {
            if (response.OK) {
              me._coreLogService.debug("LikeService.SendLikeToServer", "RequestService (response OK)", response);
              resolve(response.Data);
            } else {
              me._coreLogService.error("LikeService.SendLikeToServer", "RequestService (response not OK)", response);
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => { me._coreLogService.error("LikeService.SendLikeToServer", "RequestService", error); reject(); });
      } catch (error) {
        me._coreLogService.error("LikeService.SendLikeToServer", "try catch", error);
        reject();
      }
    });
  }

  public HasLike(contentType: ContentType, contentID: number): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me._likeObsServ.GetObject(contentType + "|" + contentID)
        .then((like) => {
          resolve(!Utils.IsNull(like) && !like.deleted);
        })
        .catch((error) => { me._coreLogService.error("LikeService.HasLike", "GetObject", error); reject(); });
    });
  }

  public GetContentIDForActiveLikes(contentType: ContentType): Promise<number[]> {
    const me = this;
    return new Promise<number[]>((resolve, reject) => {
      me._likeObsServ.GetObjectsByFilter((like) => !like.deleted && like.content_type === contentType)
        .then((likes) => {
          const contentIds: number[] = new Array();
          for (const like of likes) {
            contentIds.push(like.content_id);
          }
          resolve(contentIds);
        })
        .catch((error) => { me._coreLogService.error("LikeService.GetContentIDForActiveLikes", "GetObjectsByFilter", error); reject(); });
    });
  }

  public GetAllActiveLikes(): Promise<LikeDataModel[]> {
    const me = this;
    return new Promise<LikeDataModel[]>((resolve, reject) => {
      me._likeObsServ.GetObjectsByFilter((like) => !like.deleted)
        .then((likes) => {
          const contentIds: number[] = new Array();
          for (const like of likes) {
            contentIds.push(like.content_id);
          }
          resolve(likes);
        })
        .catch((error) => { me._coreLogService.error("LikeService.GetAllActiveLikes", "GetObjectsByFilter", error); reject(); });
    });
  }
  /**filter viewModelList by like if we route to this page from userlike page
   * @param viewModelList data list before filtering by like
   */
   public filterByLike(viewModelList: any[]) {
    const me = this;
    if (viewModelList.some(v => v.like)) {
      const filteredNews = viewModelList.filter(v => v.like);
      if (filteredNews && filteredNews.length > 0) {
        return filteredNews
      } else {
        return viewModelList;
      }
    } else {
      return viewModelList;
    }
  }

}
